import React from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import FormGroup from "@mui/material/FormGroup";
import FormHelperText from "@mui/material/FormHelperText";
import Box from "@mui/material/Box";
import { Form, Formik, FieldArray, FormikHelpers } from "formik";
import * as yup from "yup";
import LoadingButton from "@mui/lab/LoadingButton";
import { DocumentWithReviewStatus } from "../shared/DocumentsListSection";
import { PermitDocumentReviewInput } from "common/graphql";
import { PermitDocumentReviewStatus } from "common/types";

export interface RequestInfoDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (requestedDocs: PermitDocumentReviewInput[], message: string) => Promise<void>;
  documents: DocumentWithReviewStatus[];
}

interface FormValues {
  selectedDocumentIds: string[];
  message: string;
}

export const RequestInfoDialog: React.FC<RequestInfoDialogProps> = ({ open, onClose, onConfirm, documents }) => {
  const validationSchema = yup
    .object({
      selectedDocumentIds: yup.array().of(yup.string().uuid()),
      message: yup.string().required().trim(),
    })
    .test("at-least-one", "Please select at least one document or provide a message", (value): boolean => {
      const hasSelectedDocs = Boolean(value.selectedDocumentIds && value.selectedDocumentIds.length > 0);
      const hasMessage = Boolean(value.message && value.message.trim().length > 0);
      return hasSelectedDocs || hasMessage;
    });

  const initialValues: FormValues = {
    selectedDocumentIds: [],
    message: "",
  };

  const handleSubmit = async (values: FormValues, helpers: FormikHelpers<FormValues>) => {
    const documentReviews: PermitDocumentReviewInput[] = documents.map(({ id }) => {
      const reviewStatus: PermitDocumentReviewStatus = values.selectedDocumentIds.includes(id)
        ? "rejected"
        : "approved";

      return {
        id,
        review_status: reviewStatus,
      };
    });

    await onConfirm(documentReviews, values.message.trim());
    helpers.resetForm();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Request Additional Information</DialogTitle>
      <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={handleSubmit} validateOnMount>
        {(formik) => {
          const hasValidationError =
            formik.touched.selectedDocumentIds &&
            formik.touched.message &&
            formik.values.selectedDocumentIds.length === 0 &&
            !formik.values.message.trim();

          return (
            <Form>
              <DialogContent>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Select documents that need to be re-uploaded and provide additional details about what information is
                  needed.
                </Typography>

                {documents.length > 0 && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                      Documents to Re-upload
                    </Typography>
                    <FieldArray name="selectedDocumentIds">
                      {({ push, remove }) => (
                        <FormGroup>
                          {documents
                            .filter((doc) => doc.id)
                            .map((doc) => {
                              const isChecked = formik.values.selectedDocumentIds.includes(doc.id as string);
                              return (
                                <FormControlLabel
                                  key={doc.id}
                                  control={
                                    <Checkbox
                                      checked={isChecked}
                                      onChange={() => {
                                        if (isChecked) {
                                          const idx = formik.values.selectedDocumentIds.indexOf(doc.id as string);
                                          remove(idx);
                                        } else {
                                          push(doc.id);
                                        }
                                        formik.setFieldTouched("selectedDocumentIds", true, false);
                                      }}
                                    />
                                  }
                                  label={doc.documentType?.name || doc.fileName || "Document"}
                                />
                              );
                            })}
                        </FormGroup>
                      )}
                    </FieldArray>
                  </Box>
                )}

                <Box sx={{ mt: 3 }}>
                  <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                    Comment
                  </Typography>
                  <TextField
                    name="message"
                    value={formik.values.message}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    placeholder="Provide additional details about what information is needed..."
                    fullWidth
                    multiline
                    rows={4}
                  />
                </Box>

                {hasValidationError && (
                  <FormHelperText error sx={{ mt: 2 }}>
                    Please select at least one document or provide a message
                  </FormHelperText>
                )}
              </DialogContent>
              <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <LoadingButton
                  type="submit"
                  variant="contained"
                  color="warning"
                  loading={formik.isSubmitting}
                  disabled={formik.isSubmitting}
                >
                  Confirm Request
                </LoadingButton>
              </DialogActions>
            </Form>
          );
        }}
      </Formik>
    </Dialog>
  );
};

export default RequestInfoDialog;
